import type { ReactNode } from 'react';
import { Text } from '../../atoms/Text/Text';
import { KPIText, type KPITextTone } from '../KPIText/KPIText';
import styles from './KPI.module.css';

/**
 * Figma: `KPI` (441:51620) — the figure row that fills `Card`'s first slot.
 *
 * A horizontal row of label-over-value metrics `space/24` apart, separated by
 * dashed 1px rules: "NPS / 100", "Best performing / 200". The label is
 * `Text sm/leading-none/Regular` in `text/tertiary`; the value is a `_KPI Text`
 * instance, `Text md/Medium` in `text/primary` with an optional `Badge`. A
 * metric is 34 tall — 12 + 6 + 16 — and 36 when a badge pushes the value row to
 * the badge's 18.
 *
 * **`Type=lg` was removed from the set on 2026-09-14.** It was a single headline
 * figure with a badge beside it, bottom-aligned. `Type` is now a one-option
 * variant (`sm`), so it is not a prop here — the same call `InputField` makes
 * about its `Size=sm`. All 28 `KPI` instances across the file's six dashboard
 * pages are `sm`, and the pattern that replaced the headline is an ordinary `sm`
 * metric carrying a badge, which is what the callers here now use.
 *
 * **The tone axis moved onto the badge** in that same update — see `KPIText`.
 * `tone` no longer changes the figure's colour; it picks the badge's.
 *
 * Figma composes `_KPI Text` for all three metrics now, where the 2026-09 draft
 * had it on `Metric 1` only, so every value rendering through `KPIText` here
 * matches the file rather than being the generalisation it used to be.
 *
 * Renders as a `<dl>`: it is a list of terms and their values, and saying so
 * costs nothing. Not in Figma, which has no concept of a description list.
 */
export type KPITone = KPITextTone;

export type KPIMetric = {
  /** Figma's upper `Text` — the caption above the figure. */
  label?: string;
  /** Figma's lower `Text` — the figure, rendered through `KPIText`. */
  value: string;
  /**
   * Figma `_KPI Text`'s `Property 1`. Picks the badge's colour; the figure is
   * `text/primary` either way, so this is inert without a `badge`.
   */
  tone?: KPITone;
  /**
   * Figma `_KPI Text`'s `Badge?` boolean — the text inside the badge, e.g.
   * `+21.76%`. Omit it for a bare figure. Its colour comes from `tone`.
   */
  badge?: ReactNode;
};

export type KPIProps = {
  /** One entry per metric, left to right. */
  metrics: KPIMetric[];
  className?: string;
};

export function KPI({ metrics, className }: KPIProps) {
  return (
    <dl className={[styles.kpi, className].filter(Boolean).join(' ')}>
      {metrics.map((m) => (
        /*
         * A grouping div is permitted as a direct child of `<dl>` and keeps
         * each `<dt>`/`<dd>` pair together. The divider is its pseudo-element;
         * another wrapper would break description-list semantics.
         */
        <div key={m.label ?? m.value} className={styles.group}>
          {/*
           * `Text` renders *as* the `<dt>` rather than sitting inside one. A
           * wrapper would form its own line box at the inherited line-height,
           * and an inline `Text` cannot shrink it — the row came out 42 tall
           * against Figma's 34 that way.
           */}
          {m.label !== undefined && (
            <Text
              as="dt"
              className={styles.term}
              size="sm"
              leading="none"
              weight="regular"
              color="tertiary"
            >
              {m.label}
            </Text>
          )}
          {/*
           * Figma `_KPI Text`. Unlike the `<dt>`, the `<dd>` stays a real
           * wrapper — `KPIText` is its own component and cannot render as
           * one. It must not add a second line box on top of `KPIText`'s
           * own, which is what made the row 42 tall before; `.description`
           * is a flex container for exactly that reason, and a flex
           * container has no strut. See the stylesheet.
           */}
          <dd className={styles.description}>
            <KPIText tone={m.tone} badge={m.badge}>
              {m.value}
            </KPIText>
          </dd>
        </div>
      ))}
    </dl>
  );
}
